import { useCallback, useEffect, useState } from 'react';
import { useBatchId } from '../../../hooks/useBatchId';
import { useTranslation } from 'react-i18next';
import {
	PROPOSER_LOAD_REASSIGN,
	PROPOSER_LOAD_ZONES,
} from '../../../shared/Routes';
import { Button, Table, TableColumnsType, Typography } from 'antd';
import LeftArrowWhite from '../../../assets/images/arrow-left-white.svg';
import LeftArrowBlack from '../../../assets/images/arrow-left.svg';
import Icon from '../../../components/ui-components/Icon';
import {
	Batch,
	CandidateLoadDetails,
} from '../../../shared/VolunteerApplicationServiceTypes';
import { Filter } from './Filter';
import {
	resetCandidatesLoadSummaryCache,
	useProposerLoad,
} from './useProposerLoad';
import {
	getDisableBulkAssign,
	hasPrivilege,
	tablePaginationConfiguration,
} from '../../../shared/Utils';
import { PrivilegeCodes } from '../../../shared/RoleConstants';
import useUserSession from '../../../hooks/useUserSession';

const { Text } = Typography;

export const ViewProposerLoad = () => {
	const {
		candidatesLoadSummaryList,
		onPageChange,
		filter,
		onFilterChange,
		paging,
	} = useProposerLoad();

	const { pagination, zoneName, candidateLoadDetail } =
		candidatesLoadSummaryList;
	const { t } = useTranslation();
	const { loggedInUserPrivileges } = useUserSession();
	const { navigateToRoute, navigatewithZoneId } = useBatchId();
	const [status, setStatus] = useState<string | undefined>(undefined);
	const disableBulkAssign = getDisableBulkAssign();

	useEffect(() => {
		const storedBatch = sessionStorage.getItem('SELECTED_BATCH');
		if (storedBatch) {
			const parsedBatch: Batch = JSON.parse(storedBatch) as Batch;
			setStatus(parsedBatch.status);
		}
	}, []);

	const handleBackClick = useCallback(() => {
		resetCandidatesLoadSummaryCache();
		navigateToRoute(PROPOSER_LOAD_ZONES);
	}, [navigateToRoute]);

	const handleReAssign = useCallback(
		(application?: CandidateLoadDetails) => {
			sessionStorage.setItem(
				'SELECTED_APPLICATION',
				JSON.stringify(application)
			);
			navigatewithZoneId(
				PROPOSER_LOAD_REASSIGN,
				application?.candidateZoneId,
				application?.applicationId
			);
		},
		[navigatewithZoneId]
	);

	const tablePaginationConfig = tablePaginationConfiguration(
		paging,
		pagination,
		onPageChange
	);

	const showReAssign = hasPrivilege(
		PrivilegeCodes.REASSIGN_FUNCTIONARY_TO_APPLICATION,
		loggedInUserPrivileges
	);

	const baseColumns: TableColumnsType<CandidateLoadDetails> = [
		{
			title: t('candidateName'),
			dataIndex: 'candidateName',
			key: 'candidateName',
		},
		{
			title: t('candidateSrcmId'),
			dataIndex: 'candidateSrcmId',
			key: 'candidateSrcmId',
		},
		{
			title: t('candidateZoneName'),
			dataIndex: 'candidateZoneName',
			key: 'candidateZoneName',
		},
		{
			title: t('candidateGender'),
			dataIndex: 'candidateGender',
			key: 'candidateGender',
		},
		{
			title: t('proposerName'),
			dataIndex: 'proposerName',
			key: 'proposerName',
		},
		{
			title: t('proposerLoad'),
			dataIndex: 'proposerLoad',
			key: 'proposerLoad',
		},
		{
			title: t('proposerGender'),
			dataIndex: 'proposerGender',
			key: 'proposerGender',
		},
		{
			title: t('proposerSrcmId'),
			dataIndex: 'proposerSrcmId',
			key: 'proposerSrcmId',
		},
	];

	if (showReAssign) {
		baseColumns.push({
			title: t('action'),
			key: 'action',
			width: 200,
			fixed: 'right',
			render: (__, record) => (
				<Button
					block
					className="border border-primary-blue text-primary-blue font-bold mt-2"
					onClick={() => handleReAssign(record)}
					disabled={status === 'INACTIVE' ? true : false}
				>
					{record?.proposerLoad ? t('reAssign') : t('assign')}
				</Button>
			),
		});
	}

	return (
		<>
			<div className="md:hidden flex justify-center items-center pt-3 pb-1 relative">
				<Text className="font-sans font-bold text-lg mb-1">{t('view')}</Text>
			</div>
			<div className="flex justify-between items-center pt-1 pb-3 px-5 md:px-5 md:py-5 relative">
				{/* Back Button - Desktop */}
				<Button
					type="primary"
					onClick={handleBackClick}
					className="hidden md:block font-bold text-sm"
				>
					<span className="flex gap-2">
						<Icon src={LeftArrowWhite} width="w-4" height="h-4" />
						{t('back')}
					</span>
				</Button>

				{/* Back Button + Title - Mobile */}
				<div className="block sm:hidden">
					<a
						onClick={handleBackClick}
						className="relative top:0 md:top-[3px] mr-2 font-bold text-sm"
					>
						<img src={LeftArrowBlack} width={'w-4'} height={'h-4'} />
					</a>
				</div>

				{/* Centered Title */}
				<Text className="hidden md:block font-sans font-bold text-xl mb-1 absolute left-1/2 transform -translate-x-1/2">
					{`${t('ProposerLoadDetailsofZone')} - ${zoneName}`}
				</Text>

				<Filter
					value={filter}
					onFilterChange={onFilterChange}
					disableBulkAssign={disableBulkAssign}
				/>
			</div>
			<div className="hidden md:block px-4">
				<div className="border rounded-xl shadow-sm bg-white">
					<Table
						columns={baseColumns}
						dataSource={candidateLoadDetail}
						rowKey="key"
						pagination={tablePaginationConfig}
						scroll={{ x: 'max-content' }}
						rowClassName={() => 'border-b border-gray-300 bg-white'}
						className="w-full overflow-x-auto !border-separate !border-spacing-y-0"
					/>
				</div>
			</div>
		</>
	);
};
